import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/authContext';
import { onProjectChange, updateProjectDetails } from '../../firebase/projects';
import FileBrowser from '../file-browser';
import Moodboard from '../moodboard';
import { Lock, MapPin, Sparkles, MessageSquare } from 'lucide-react';
import { AdvancedColorPicker } from '../moodboard/AdvancedColorPicker';
import { clearAllProjectMessages } from '../../firebase/messages';
import { onNodesChange } from '../../firebase/files';


const ClearFeedbackModal = ({ isOpen, onClose, onConfirm, projectName, isClearing }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Confirm Action</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Are you sure you want to permanently delete all feedback messages and attachments for the project "{projectName}"? This action cannot be undone.
                </p>
                <div className="mt-6 flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        disabled={isClearing}
                        className="px-4 py-2 rounded-lg font-medium bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isClearing}
                        className="px-4 py-2 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                    >
                        {isClearing ? 'Clearing...' : 'Clear Feedback'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ProjectSettings = ({ project, projectId, isOwner }) => {
    const { currentUser } = useAuth();
    const canManage = isOwner && currentUser.role !== 'customer';
    
    const [formData, setFormData] = useState({
        name: project.name || '',
        description: project.description || '',
        location: project.location || '',
        area: project.area || '',
        status: project.status || 'Planning',
        imageUrl: project.imageUrl || '',
        defaultPlanId: project.defaultPlanId || '',
        masterStyle: {
            image: {
                borderRadius: project.masterStyle?.image?.borderRadius ?? 8,
                borderWidth: project.masterStyle?.image?.borderWidth ?? 0,
                borderColor: project.masterStyle?.image?.borderColor ?? '#000000',
                colorOverlayEnabled: project.masterStyle?.image?.colorOverlayEnabled ?? false,
                colorOverlay: project.masterStyle?.image?.colorOverlay ?? '#000000',
                colorOverlayOpacity: project.masterStyle?.image?.colorOverlayOpacity ?? 0.4,
                linkColor: project.masterStyle?.image?.linkColor ?? '#000000',
                linkWidth: project.masterStyle?.image?.linkWidth ?? 2,
            },
            area: {
                fillColor: project.masterStyle?.area?.fillColor ?? '#3b82f6',
                fillOpacity: project.masterStyle?.area?.fillOpacity ?? 0.05,
            }
        },
        settings: {
            autoSaveMoodboard: project.settings?.autoSaveMoodboard ?? true,
        },
    });
    const [uploadMethod, setUploadMethod] = useState('url');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isApplyingStyles, setIsApplyingStyles] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isClearFeedbackModalOpen, setIsClearFeedbackModalOpen] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [floorPlans, setFloorPlans] = useState([]);

    useEffect(() => {
        if (!projectId) return;
        const unsubscribe = onNodesChange(projectId, "moodboard-floorplans", (plans) => {
            setFloorPlans(plans);
        });
        return () => unsubscribe();
    }, [projectId]);

    useEffect(() => {
        if (project.imageUrl && project.imageUrl.startsWith('data:image')) {
            setUploadMethod('file');
        } else {
            setUploadMethod('url');
        }
    }, [project.imageUrl]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'autoSaveMoodboard') {
            setFormData(prev => ({ ...prev, settings: { ...prev.settings, autoSaveMoodboard: checked } }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleMasterStyleChange = (category, key, value) => {
        setFormData(prev => ({
            ...prev,
            masterStyle: {
                ...prev.masterStyle,
                [category]: {
                    ...(prev.masterStyle?.[category] || {}),
                    [key]: value,
                }
            },
        }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, imageUrl: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleApplyMasterStyle = async () => {
        if (!canManage) return;

        setIsApplyingStyles(true);
        setMessage({ type: '', text: '' });

        try {
            const masterImageStyle = formData.masterStyle.image;
            const masterAreaStyle = formData.masterStyle.area;
            const currentMoodboardState = project.moodboardState || {};
            
            const newMoodboardState = {};

            for (const planId in currentMoodboardState) {
                if (Object.prototype.hasOwnProperty.call(currentMoodboardState, planId)) {
                    newMoodboardState[planId] = currentMoodboardState[planId].map(entity => {
                        if (entity.type === 'image') {
                            return {
                                ...entity,
                                borderRadius: masterImageStyle.borderRadius,
                                borderWidth: masterImageStyle.borderWidth,
                                borderColor: masterImageStyle.borderColor,
                                colorOverlayEnabled: masterImageStyle.colorOverlayEnabled,
                                colorOverlay: masterImageStyle.colorOverlay,
                                colorOverlayOpacity: masterImageStyle.colorOverlayOpacity,
                                linkColor: masterImageStyle.linkColor,
                                linkWidth: masterImageStyle.linkWidth,
                            };
                        }
                        if (['polygon', 'rect', 'ellipse'].includes(entity.type)) {
                            return {
                                ...entity,
                                fillColor: masterAreaStyle.fillColor,
                                fillOpacity: masterAreaStyle.fillOpacity,
                            };
                        }
                        return entity;
                    });
                }
            }

            await updateProjectDetails(projectId, { moodboardState: newMoodboardState, masterStyle: formData.masterStyle });
            setMessage({ type: 'success', text: 'Master styles applied successfully to all elements.' });
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to apply master styles.' });
            console.error(err);
        } finally {
            setIsApplyingStyles(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canManage) return;

        setIsSubmitting(true);
        setMessage({ type: '', text: '' });
        try {
            await updateProjectDetails(projectId, formData);
            setMessage({ type: 'success', text: 'Project updated successfully!' });
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to update project. Please try again.' });
            console.error(err);
        } finally {
            setIsSubmitting(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        }
    };
    
    const handleClearFeedback = async () => {
        if (!canManage) return;

        setIsClearing(true);
        setMessage({ type: '', text: '' });

        try {
            await clearAllProjectMessages(projectId);
            setMessage({ type: 'success', text: 'All moodboard feedback has been cleared.' });
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to clear feedback.' });
            console.error(err);
        } finally {
            setIsClearing(false);
            setIsClearFeedbackModalOpen(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 p-6 sm:p-8 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Project Details Section */}
                <div>
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4 border-b pb-2 dark:border-gray-600">Project Details</h2>
                    <div className="space-y-6">
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Project Name</label>
                                <input type="text" name="name" value={formData.name} onChange={handleChange} required disabled={!canManage} className="w-full mt-2 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-700/50" />
                            </div>
                            <div>
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Status</label>
                                <select name="status" value={formData.status} onChange={handleChange} disabled={!canManage} className="w-full mt-2 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-700/50">
                                    {['Planning', 'In Progress', 'Completed', 'On Hold'].map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Location</label>
                                <div className="relative mt-2">
                                    <input
                                        type="text"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleChange}
                                        placeholder="e.g. 123 Main St, Anytown, USA"
                                        disabled={!canManage}
                                        className="w-full px-3 pr-10 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-700/50" />
                                    {formData.location && (
                                        <a
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formData.location)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-blue-500"
                                        >
                                            <MapPin className="h-5 w-5" />
                                        </a>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Area (sq. meters)</label>
                                <input type="number" name="area" value={formData.area} onChange={handleChange} disabled={!canManage} className="w-full mt-2 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-700/50" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Description</label>
                                <textarea name="description" value={formData.description} onChange={handleChange} rows="3" disabled={!canManage} className="w-full mt-2 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-700/50" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block mb-2">Project Image (Optional)</label>
                                {canManage && (
                                    <div className="flex items-center space-x-4 mb-2">
                                        <label className="flex items-center cursor-pointer">
                                            <input type="radio" name="uploadMethod" value="file" checked={uploadMethod === 'file'} onChange={() => { setUploadMethod('file'); setFormData(p=>({...p, imageUrl: ''})) }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" />
                                            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Upload File</span>
                                        </label>
                                        <label className="flex items-center cursor-pointer">
                                            <input type="radio" name="uploadMethod" value="url" checked={uploadMethod === 'url'} onChange={() => { setUploadMethod('url'); setFormData(p=>({...p, imageUrl: ''})) }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" />
                                            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">From URL</span>
                                        </label>
                                    </div>
                                )}
                                {uploadMethod === 'file' && canManage ? (
                                    <input type="file" key={Date.now()} accept="image/*" onChange={handleImageUpload} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:text-gray-400 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600 cursor-pointer" />
                                ) : (
                                    <input type="url" name="imageUrl" value={formData.imageUrl} onChange={handleChange} placeholder="https://example.com/image.png" disabled={!canManage || uploadMethod === 'file'} className="w-full px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-700/50" />
                                )}
                                {formData.imageUrl && <img src={formData.imageUrl} alt="Project Preview" className="mt-4 rounded-lg max-h-48" />}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Moodboard Settings Section */}
                <div>
                     <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4 border-b pb-2 dark:border-gray-600">Moodboard Settings</h2>
                     <div className="space-y-6">
                        <label htmlFor="autoSaveToggle" className="flex items-center justify-between p-4 border rounded-lg dark:border-gray-700 cursor-pointer">
                            <div>
                                <span className="font-medium text-gray-700 dark:text-gray-300">Enable Auto-Save</span>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Automatically save changes made on the moodboard canvas.</p>
                            </div>
                            <input
                                id="autoSaveToggle"
                                type="checkbox"
                                name="autoSaveMoodboard"
                                checked={formData.settings.autoSaveMoodboard}
                                onChange={handleChange}
                                disabled={!canManage}
                                className="sr-only peer"
                            />
                            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-500 peer-checked:bg-blue-600"></div>
                        </label>
                        <div>
                            <label htmlFor="defaultPlan" className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Default Active Plan</label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">Select which plan is active when the moodboard is opened.</p>
                            <select
                                id="defaultPlan"
                                name="defaultPlanId"
                                value={formData.defaultPlanId || ''}
                                onChange={handleChange}
                                disabled={!canManage || floorPlans.length === 0}
                                className="w-full px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-700/50"
                            >
                                <option value="">{floorPlans.length === 0 ? 'No plans available' : 'Select a default plan...'}</option>
                                {floorPlans.map(plan => (
                                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Master Style Section */}
                <div>
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4 border-b pb-2 dark:border-gray-600">Moodboard Master Styles</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Set the default appearance for new images added to the moodboard.</p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="space-y-6">
                             {/* Border Style */}
                            <div className="p-4 border dark:border-gray-700 rounded-lg">
                                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">Image Border</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Corner Radius</label>
                                        <input type="number" min="0" value={formData.masterStyle.image.borderRadius} onChange={(e) => handleMasterStyleChange('image', 'borderRadius', Number(e.target.value))} disabled={!canManage} className="w-full mt-2 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50" />
                                    </div>
                                    <div>
                                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Border Width</label>
                                        <input type="number" min="0" value={formData.masterStyle.image.borderWidth} onChange={(e) => handleMasterStyleChange('image', 'borderWidth', Number(e.target.value))} disabled={!canManage} className="w-full mt-2 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Border Color</label>
                                        <AdvancedColorPicker
                                            value={formData.masterStyle.image.borderColor}
                                            onChange={(color) => handleMasterStyleChange('image', 'borderColor', color)}
                                            disabled={!canManage || formData.masterStyle.image.borderWidth === 0}
                                        />
                                    </div>
                                </div>
                            </div>
                             {/* Overlay Style */}
                            <div className="p-4 border dark:border-gray-700 rounded-lg">
                                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">Image Color Overlay</h4>
                                <label className="flex items-center justify-between cursor-pointer mb-4">
                                    <span className="font-medium text-gray-700 dark:text-gray-300">Enable Overlay by Default</span>
                                    <input type="checkbox" checked={formData.masterStyle.image.colorOverlayEnabled} onChange={(e) => handleMasterStyleChange('image', 'colorOverlayEnabled', e.target.checked)} disabled={!canManage} className="sr-only peer" />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                                <div>
                                    <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Overlay Color</label>
                                    <AdvancedColorPicker
                                        value={formData.masterStyle.image.colorOverlay}
                                        onChange={(color) => handleMasterStyleChange('image', 'colorOverlay', color)}
                                        disabled={!canManage}
                                    />
                                </div>
                                <div className="mt-4">
                                    <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Overlay Opacity</label>
                                    <input type="range" min="0" max="1" step="0.05" value={formData.masterStyle.image.colorOverlayOpacity} onChange={(e) => handleMasterStyleChange('image', 'colorOverlayOpacity', Number(e.target.value))} disabled={!canManage} className="w-full mt-1 accent-blue-600" />
                                </div>
                            </div>
                             {/* Link Style */}
                            <div className="p-4 border dark:border-gray-700 rounded-lg">
                                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">Image Area Link Line</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                     <div>
                                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Link Width</label>
                                        <input type="number" min="0" value={formData.masterStyle.image.linkWidth} onChange={(e) => handleMasterStyleChange('image', 'linkWidth', Number(e.target.value))} disabled={!canManage} className="w-full mt-2 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Link Color</label>
                                        <AdvancedColorPicker
                                            value={formData.masterStyle.image.linkColor}
                                            onChange={(color) => handleMasterStyleChange('image', 'linkColor', color)}
                                            disabled={!canManage}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 border dark:border-gray-700 rounded-lg">
                                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">Area Fill</h4>
                                <div>
                                    <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Default Fill Color</label>
                                    <AdvancedColorPicker
                                        value={formData.masterStyle.area.fillColor}
                                        onChange={(color) => handleMasterStyleChange('area', 'fillColor', color)}
                                        disabled={!canManage}
                                    />
                                </div>
                                <div className="mt-4">
                                    <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Default Fill Opacity</label>
                                    <div className="flex items-center gap-2">
                                        <input 
                                            type="range" 
                                            min="0" max="1" step="0.05" 
                                            value={formData.masterStyle.area.fillOpacity} 
                                            onChange={(e) => handleMasterStyleChange('area', 'fillOpacity', Number(e.target.value))}
                                            disabled={!canManage} 
                                            className="w-full mt-1 accent-blue-600"
                                        />
                                        <span className="text-sm font-mono text-gray-500 dark:text-gray-400 w-12 text-right">{Math.round(formData.masterStyle.area.fillOpacity * 100)}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col">
                             <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block mb-2">Live Preview</label>
                             <div className="relative w-full flex-grow flex items-center justify-center bg-gray-100 dark:bg-gray-900 rounded-lg p-4 min-h-[20rem]">
                                <div 
                                    className="relative bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 flex items-center justify-center font-semibold text-lg"
                                    style={{
                                        width: 180,
                                        height: 180,
                                        borderRadius: `${formData.masterStyle.image.borderRadius}px`,
                                        border: `${formData.masterStyle.image.borderWidth}px solid ${formData.masterStyle.image.borderColor}`,
                                    }}
                                >
                                    <span>Image</span>
                                    {formData.masterStyle.image.colorOverlayEnabled && (
                                        <div 
                                            className="absolute inset-0"
                                            style={{
                                                backgroundColor: formData.masterStyle.image.colorOverlay,
                                                opacity: formData.masterStyle.image.colorOverlayOpacity,
                                                borderRadius: `${formData.masterStyle.image.borderRadius}px`,
                                            }}
                                        ></div>
                                    )}
                                </div>
                                <svg className="absolute top-0 left-0 w-full h-full" style={{ pointerEvents: 'none' }}>
                                    <line 
                                        x1="25%" y1="50%" 
                                        x2="calc(50% - 90px - 10px)" y2="50%" 
                                        stroke={formData.masterStyle.image.linkColor} 
                                        strokeWidth={formData.masterStyle.image.linkWidth}
                                        strokeDasharray="4"
                                    />
                                    <circle cx="25%" cy="50%" r="5" fill={formData.masterStyle.image.linkColor} />
                                </svg>
                             </div>
                        </div>
                    </div>
                </div>

                {canManage && (
                    <div className="border-t dark:border-gray-700 pt-6">
                         <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                                 <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/50 rounded-lg flex items-center justify-center">
                                    <Sparkles className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                                </div>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Apply Styles to All Elements</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">This will apply the master styles configured above to all existing elements on the moodboard, overwriting any individual customizations. <span className="font-semibold">This action cannot be undone.</span></p>
                                <button 
                                    type="button" 
                                    onClick={handleApplyMasterStyle}
                                    disabled={isApplyingStyles || isSubmitting}
                                    className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors ${isApplyingStyles || isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700'}`}
                                >
                                    {isApplyingStyles ? 'Applying...' : 'Apply Master Styles to All'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                {canManage && (
                    <div className="border-t dark:border-gray-700 pt-8 mt-8">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4 border-b pb-2 dark:border-gray-600">Danger Zone</h2>
                        <div className="flex items-start gap-4 p-4 border border-red-500/30 dark:border-red-500/30 rounded-lg bg-red-50/50 dark:bg-red-900/10">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/50 rounded-lg flex items-center justify-center">
                                    <MessageSquare className="h-6 w-6 text-red-600 dark:text-red-400" />
                                </div>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-red-700 dark:text-red-300">Clear All Moodboard Feedback</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 mb-4">This will permanently delete all comments and attachments across all moodboard elements for this project. This action cannot be undone.</p>
                                <button 
                                    type="button" 
                                    onClick={() => setIsClearFeedbackModalOpen(true)}
                                    disabled={isApplyingStyles || isSubmitting || isClearing}
                                    className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed`}
                                >
                                    {isClearing ? 'Clearing...' : 'Clear All Feedback'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}


                {canManage && (
                    <div className="flex justify-end pt-8 mt-8 border-t dark:border-gray-700">
                        <button type="submit" disabled={isSubmitting || isApplyingStyles || isClearing} className={`px-6 py-2.5 text-white font-medium rounded-lg ${isSubmitting || isApplyingStyles || isClearing ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}>
                            {isSubmitting ? 'Saving...' : 'Save All Settings'}
                        </button>
                    </div>
                )}
                 {message.text && (
                    <p className={`text-sm text-center ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{message.text}</p>
                 )}
            </form>
            <ClearFeedbackModal
                isOpen={isClearFeedbackModalOpen}
                onClose={() => setIsClearFeedbackModalOpen(false)}
                onConfirm={handleClearFeedback}
                projectName={project.name}
                isClearing={isClearing}
            />
        </div>
    );
};

const ProjectDetail = () => {
    const { projectId, tab } = useParams();
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState(tab || '');

    useEffect(() => {
        if (!projectId || !currentUser) return;

        const unsubscribe = onProjectChange(projectId, (projectData) => {
            if (projectData) {
                setProject(projectData);
            } else {
                setError('Project not found or you do not have access.');
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [projectId, currentUser]);
    
    const isOwner = project?.userId === currentUser?.uid;
    const userPermissions = !isOwner && project?.permissions ? project.permissions[currentUser.uid] : null;

    const canAccessFiles = isOwner || userPermissions?.files;
    const canAccessMoodboard = isOwner || userPermissions?.moodboard;
    const canAccessSettings = isOwner;

    useEffect(() => {
        if (loading) return;

        // Determine the effective tab
        let currentTab = tab;
        if (!currentTab) {
             if (canAccessMoodboard) currentTab = 'moodboard';
             else if (canAccessFiles) currentTab = 'files';
             else if (canAccessSettings) currentTab = 'settings';
             else currentTab = '';
        }
        
        if (!tab && currentTab) {
            navigate(`/projects/${projectId}/${currentTab}`, { replace: true });
        }
        setActiveTab(currentTab);

    }, [tab, loading, canAccessFiles, canAccessMoodboard, canAccessSettings, projectId, navigate]);

    if (loading) {
        return <div className="flex justify-center items-center h-screen">Loading project...</div>;
    }

    if (error) {
        return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;
    }

    if (!project) {
        return <div className="flex justify-center items-center h-screen">Project not found.</div>;
    }

    return (
        <div className="pt-16 w-full h-full flex flex-col">
            <div className="w-full flex-grow min-h-0 p-4 sm:p-6 lg:p-8 overflow-y-auto">
                {activeTab === 'files' && canAccessFiles && (
                     <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 h-full">
                        <FileBrowser projectId={projectId} projectOwnerId={project.userId} />
                    </div>
                )}
                {activeTab === 'moodboard' && canAccessMoodboard && (
                    <Moodboard projectId={projectId} projectOwnerId={project.userId} project={project} />
                )}
                 {activeTab === 'settings' && canAccessSettings && (
                    <ProjectSettings project={project} projectId={projectId} isOwner={isOwner} />
                )}
                
                {activeTab && !canAccessFiles && !canAccessMoodboard && !canAccessSettings && (
                     <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center h-full">
                        <Lock className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mt-4">Access Denied</h3>
                        <p className="text-gray-500 dark:text-gray-400 mt-1">You do not have permission to access this section.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectDetail;
